import { Check, SparklesIcon, Warning } from 'assets/icons';
import Button from 'components/button';
import { ORANGE_COLORS } from 'utils/colors';
import {
  AI_GENERATION_STEPS,
  GENERATION_STEP_STATE,
  countDoneGenerationSteps,
  countRiskFactors,
  countSentences,
  getGenerationStepState,
} from './qualificationAiSuggestion';
import {
  qualificationCountText,
  qualificationText,
} from './qualificationTexts';

const aiText = (key, values) => qualificationText(`ai.${key}`, values);

const WARNING_TEXT_COLOR = '#975612'; // yellow-700
const ALERT_ICON_COLOR = '#374151'; // gray-700

const warningBoxClass =
  'rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700';

const sectorPillClass =
  'rounded border border-yellow-200 bg-yellow-50 px-1.5 py-0.5 text-xs text-yellow-800';

/** What the AI fills from the title, listed on the first step. */
export const AiFieldPills = () => (
  <ul className="flex flex-wrap gap-2">
    {AI_GENERATION_STEPS.map((step) => (
      <li key={step.id} className={sectorPillClass}>
        {aiText(`steps.${step.id}`)}
      </li>
    ))}
  </ul>
);

const countText = (key, count) => {
  if (count === 0) return aiText(`${key}Zero`);
  return qualificationCountText(`ai.${key}`, count);
};

const requirementsSummary = (progress) => {
  const hasRequirements =
    progress.conditions ||
    Array.isArray(progress.tools) ||
    Array.isArray(progress.licences);
  if (!hasRequirements) return '';
  const factors = countRiskFactors(progress.conditions);
  return [
    factors > 0
      ? qualificationCountText('ai.stepFactors', factors)
      : aiText('stepNotAtRisk'),
    qualificationCountText('ai.stepTools', progress.tools?.length ?? 0),
    qualificationCountText('ai.stepLicences', progress.licences?.length ?? 0),
  ].join(' · ');
};

/** The detail line of a finished step, from what the api already sent. */
const stepDetail = (stepId, progress, sectorLabel) => {
  if (!progress) return '';
  if (stepId === 'description') {
    const sentences = countSentences(progress.descriptionHtml);
    return sentences > 0
      ? qualificationCountText('ai.stepSentences', sentences)
      : '';
  }
  if (stepId === 'sector') {
    const type = progress.sector?.type;
    return type ? sectorLabel(type) : (progress.sector?.label ?? '');
  }
  if (stepId === 'tasks') {
    return Array.isArray(progress.tasks)
      ? countText('stepTasks', progress.tasks.length)
      : '';
  }
  if (stepId === 'codes') {
    return [
      progress.rome?.code && aiText('stepRome', { code: progress.rome.code }),
      progress.pcs?.code && aiText('stepPcs', { code: progress.pcs.code }),
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (stepId === 'requirements') return requirementsSummary(progress);
  return '';
};

const StepIcon = ({ state }) => {
  if (state === GENERATION_STEP_STATE.DONE) {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-500">
        <Check wh={12} color="#FFFFFF" />
      </span>
    );
  }
  if (state === GENERATION_STEP_STATE.IN_PROGRESS) {
    return (
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-yellow-500 motion-reduce:animate-none" />
    );
  }
  return (
    <span className="h-5 w-5 rounded-full border-2 border-dashed border-gray-300" />
  );
};

const STEP_TITLE_CLASS = {
  [GENERATION_STEP_STATE.DONE]: 'font-medium text-gray-900',
  [GENERATION_STEP_STATE.IN_PROGRESS]: 'font-medium text-yellow-600',
  [GENERATION_STEP_STATE.WAITING]: 'text-gray-400',
};

const stepSecondLine = (state, detail) => {
  if (state === GENERATION_STEP_STATE.IN_PROGRESS) return aiText('stepPending');
  if (state === GENERATION_STEP_STATE.WAITING) return aiText('stepWaiting');
  return detail;
};

/**
 * The generation steps, in the order the api runs them, each following the
 * status the api reports. `progress` is the last answer read, for the detail
 * a finished step shows.
 */
export const AiGenerationSteps = ({ status, progress, sectorLabel }) => {
  const doneCount = countDoneGenerationSteps(status);
  const total = AI_GENERATION_STEPS.length;
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <SparklesIcon wh={18} color={ORANGE_COLORS[500]} />
          {aiText('generatingTitle')}
        </div>
        <span
          data-testid="ai-steps-counter"
          className="rounded-md bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700"
        >
          <span aria-hidden="true">{`${doneCount}/${total}`}</span>
          <span className="sr-only" role="status">
            {aiText('counterLabel', { done: doneCount, total })}
          </span>
        </span>
      </div>
      <ol className="flex flex-col" aria-label={aiText('generatingTitle')}>
        {AI_GENERATION_STEPS.map((step, index) => {
          const state = getGenerationStepState(step, status);
          const isDone = state === GENERATION_STEP_STATE.DONE;
          const isLast = index === total - 1;
          const secondLine = stepSecondLine(
            state,
            isDone ? stepDetail(step.id, progress, sectorLabel) : ''
          );
          return (
            <li
              key={step.id}
              data-testid={`ai-step-${step.id}`}
              data-done={isDone ? 'true' : 'false'}
              data-state={state}
              className={`relative flex gap-3 ${isLast ? '' : 'pb-3'}`}
            >
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute bottom-0 left-[9px] top-6 w-0.5 ${
                    isDone ? 'bg-yellow-500' : 'bg-gray-200'
                  }`}
                />
              )}
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <StepIcon state={state} />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className={`text-sm ${STEP_TITLE_CLASS[state]}`}>
                  {aiText(`stepTitles.${step.id}`)}
                </span>
                {secondLine && (
                  <span
                    className={`text-xs ${
                      state === GENERATION_STEP_STATE.WAITING
                        ? 'text-gray-400'
                        : 'text-gray-500'
                    }`}
                  >
                    {secondLine}
                  </span>
                )}
                {isDone && (
                  <span className="sr-only">{aiText('stepDone')}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const AiPrefilledBanner = ({ onRegenerate, disabled }) => (
  <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
    <span className="flex items-center gap-2">
      <SparklesIcon wh={18} color={ORANGE_COLORS[500]} />
      <span>{aiText('prefilled')}</span>
    </span>
    <Button
      color={disabled ? 'DISABLED' : 'PRIMARY'}
      size="SM"
      className="shrink-0"
      onClick={onRegenerate}
      disabled={disabled}
    >
      <SparklesIcon wh={16} color={disabled ? '#9CA3AF' : '#FFFFFF'} />
      {aiText('regenerate')}
    </Button>
  </div>
);

export const AiFailureMessage = () => (
  <div
    role="alert"
    className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
  >
    {aiText('failed')}
  </div>
);

/** Low confidence on a code: a warning, each alternative applying its code on click. */
export const LowConfidenceAlert = ({ field, alternatives, onPick }) => (
  <div
    role="note"
    data-testid={`ai-low-confidence-${field}`}
    className={warningBoxClass}
  >
    <div className="flex items-start gap-2">
      <span className="mt-0.5 shrink-0">
        <Warning wh={16} color={WARNING_TEXT_COLOR} />
      </span>
      <span>{aiText(`lowConfidence.${field}`)}</span>
    </div>
    {alternatives.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {alternatives.map((alternative) => (
          <Button
            key={alternative.code}
            color="SECONDARY"
            size="SM"
            className="text-left"
            onClick={() => onPick(alternative)}
          >
            {aiText('alternative', {
              code: alternative.code,
              label: alternative.label ?? '',
            })}
          </Button>
        ))}
      </div>
    )}
  </div>
);

const similarQualificationHref = (name) =>
  `/qualification?search=${encodeURIComponent(name)}`;

/** Existing qualifications close to the proposal; the AI decides nothing here, the api lists them. */
export const SimilarQualificationsAlert = ({ qualifications, sectorLabel }) => (
  <div
    role="note"
    data-testid="ai-similar-qualifications"
    className="rounded-lg border border-gray-200 bg-white p-4 text-sm"
  >
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0">
        <Warning wh={18} color={ALERT_ICON_COLOR} />
      </span>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-gray-900">
            {aiText('similarTitle')}
          </span>
          <span className="text-gray-500">{aiText('similarHelp')}</span>
        </div>
        <ul className="flex list-disc flex-col gap-1.5 pl-4 marker:text-gray-400">
          {qualifications.map((qualification) => {
            const isDisabled = qualification.isActive === false;
            return (
              <li key={qualification.id ?? qualification.name}>
                <span className="flex flex-wrap items-center gap-2">
                  <a
                    href={similarQualificationHref(qualification.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`underline ${
                      isDisabled ? 'text-gray-400' : 'text-gray-900'
                    }`}
                  >
                    {qualification.name}
                  </a>
                  {qualification.type && (
                    <span className={sectorPillClass}>
                      {sectorLabel(qualification.type)}
                    </span>
                  )}
                  {isDisabled && (
                    <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-600">
                      {aiText('similarDisabled')}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  </div>
);
